import { Component, ElementRef, HostBinding, HostListener, Input, OnDestroy, OnInit } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { fuseAnimations } from '@fuse/animations';
import { FuseConfigService } from '@fuse/services/config.service';
import { FuseNavigationService } from '@fuse/components/navigation/navigation.service';

@Component({
    selector   : 'fuse-nav-horizontal-collapsable',
    templateUrl: './collapsable.component.html',
    styleUrls  : ['./collapsable.component.scss'],
    animations : fuseAnimations
})
export class FuseNavHorizontalCollapsableComponent implements OnInit, OnDestroy
{
    fuseConfig: any;
    isOpen = false;

    @HostBinding('class')
    classes = 'nav-collapsable nav-item';

    @Input()
    item: any;

    // Private
    private _unsubscribeAll: Subject<any>;

    constructor(
        private _elementRef: ElementRef,
        private _fuseConfigService: FuseConfigService,
        private _fuseNavigationService: FuseNavigationService
    )
    {
        // Set the private defaults
        this._unsubscribeAll = new Subject();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void
    {
        // Subscribe to config changes
        this._fuseConfigService.config
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(
                (config: any) => {
                    this.fuseConfig = config;
                }
            );

        this._fuseNavigationService.onItemCollapsed
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((clickedItem: any) => {
                if ( clickedItem === null )
                {
                    this.isOpen = false;
                    return;
                }

                if ( clickedItem.children && !this.isChildrenOf(this.item, clickedItem) && this.item !== clickedItem )
                {
                    this.isOpen = false;
                }
            });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void
    {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(true);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Open
     */
    @HostListener('mouseenter')
    open(): void
    {
        this.isOpen = true;
        this._fuseNavigationService.onItemCollapsed.next(this.item);
    }

    @HostListener('document:mousedown', ['$event'])
    onDocumentClick(event: Event): void
    {
        if ( this.isOpen && !this._elementRef.nativeElement.contains(event.target as Node) )
        {
            this._fuseNavigationService.onItemCollapsed.next(null);
        }
    }

    @HostListener('document:keydown.escape', ['$event'])
    onDocumentEscape(event: KeyboardEvent): void
    {
        if ( this.isOpen )
        {
            this._fuseNavigationService.onItemCollapsed.next(null);
        }
    }

    isChildrenOf(parent: any, item: any): boolean
    {
        if ( !parent.children )
        {
            return false;
        }

        if ( parent.children.indexOf(item) !== -1 )
        {
            return true;
        }

        for ( const children of parent.children )
        {
            if ( children.children && this.isChildrenOf(children, item) )
            {
                return true;
            }
        }

        return false;
    }
}
